import { Component, ComponentFactoryResolver, ComponentRef, EventEmitter, HostBinding, HostListener, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ModalTexts } from '../modal-texts';
import { PopoverInfoIconComponent } from '../../popover-info-icon/popover-info-icon.component';
import { ShadowDomConfig } from '@app/core/directives/shadow-dom.directive';


// Most of this copied from projects/comp-lib/src/lib/components/modal/ngx-smart-modal.component.ts

@Component({
  selector: 'app-rad-base-modal',
  templateUrl: './rad-base-modal.component.html',
  styleUrls: ['./rad-base-modal.component.scss'],
  host: {
    '[style.--modal-z-index]': 'modalZIndex'
  }
})
export class RadBaseModalComponent implements OnInit {
  @ViewChild('radModal', { static: false }) public modal: any;

  @Input() closable: boolean = true;
  @Input() closeButtonText: string | null = ModalTexts.CANCEL_BUTTON_TEXT;
  @Input() showCloseButton: boolean | null = true;
  @Input() confirmButtonText: string | null = ModalTexts.CONFIRM_BUTTON_TEXT;
  @Input() showConfirmButton: boolean | null = true;
  @Input() disableConfirmButton: boolean = false;
  @Input() disableCloseButton: boolean = false;
  @Input() showNewButton: boolean | null = false;
  @Input() newButtonText: string | null = "";

  @Input() identifier: string = "default";
  @Input() title: string | null = "";
  @Input() dismissible: boolean = true;

  @Input() modalZIndex: number = 9999;
  @Input() titleInfoPopoverContent: string | null = null;

  @Input() modalHeight: string = '80vh';
  @Input() modalWidth: string = '1200px';
  @Input() contentMaxHeight: string = null;
  @Input() contentMarginTop: string = '28px';
  @Input() modalMarginTop: string = '14vh';

  @HostBinding('style.--modal-height')
  get hostModalHeight(): string {
    return this.modalHeight;
  }

  @HostBinding('style.--modal-width')
  get hostModalWidth(): string {
    return this.modalWidth;
  }

  @HostBinding('style.--modal-margin-top')
  get hostModalMarginTop(): string {
    return this.modalMarginTop;
  }

  @HostBinding('style.--modal-content-max-height')
  get hostContentMaxHeight(): string {
    return this.contentMaxHeight || `calc(${this.modalHeight} - 160px)`;
  }

  @HostBinding('style.--modal-content-margin-top')
  get hostContentMarginTop(): string {
    return this.contentMarginTop;
  }

  @Output() public onConfirm: EventEmitter<any> = new EventEmitter();
  @Output() public onClose: EventEmitter<any> = new EventEmitter();
  @Output() public onNew: EventEmitter<any> = new EventEmitter();
  @Output() public onDismiss: EventEmitter<any> = new EventEmitter();

  // Not sure if we need this for this component
  @Output() public onDismissFinished: EventEmitter<any> = new EventEmitter();
  @Output() public onCloseFinished: EventEmitter<any> = new EventEmitter();

  @Output() public onDataAdded: EventEmitter<any> = new EventEmitter();
  @Output() public onDataRemoved: EventEmitter<any> = new EventEmitter();

  private _data: any;
  private helpIconComponentRef: ComponentRef<PopoverInfoIconComponent> | null = null;
  public shadowDomConfig: ShadowDomConfig[] | null = null;
  private resizeTimer: any = null;

  @HostListener('window:resize')
  onWindowResize(): void {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.contentChange(), 150);
  }

  constructor(
    private resolver: ComponentFactoryResolver,
    private injector: Injector
  ) {}

  ngOnInit() {}

  ngAfterViewInit(): void {
    this.open();
    
    // Create and inject help icon component if titleInfoPopoverContent is provided
    if (this.titleInfoPopoverContent) {
      this.createHelpIconComponent();
    }
  }

  private createHelpIconComponent(): void {
    // Create the component dynamically
    const factory = this.resolver.resolveComponentFactory(PopoverInfoIconComponent);
    this.helpIconComponentRef = factory.create(this.injector);
    
    // Set the input properties
    this.helpIconComponentRef.instance.helpText = this.titleInfoPopoverContent;
    this.helpIconComponentRef.instance.popoverPlacement = 'right';
    this.helpIconComponentRef.instance.popoverAppendTo = null;
    
    // Trigger change detection
    this.helpIconComponentRef.changeDetectorRef.detectChanges();
    
    // Get the native DOM element
    const helpIconElement = this.helpIconComponentRef.location.nativeElement;
    
    // Configure shadow DOM injection: inject styles first, then the component
    this.shadowDomConfig = [
      {
        action: 'add',
        selector: '.rad-modal__title',
        html: `<style>
          rad-icon::part(svg) {
            --rad-icon-color-fill-light: #4E60EE;
          }
        </style>`,
        position: 'append'
      },
      {
        action: 'add',
        selector: '.rad-modal__title',
        element: helpIconElement,
        position: 'append'
      }
    ];
  }

  public hasData(): boolean {
    return this._data !== undefined;
  }

  public setData(data: any, force?: boolean): RadBaseModalComponent {
    if (!this.hasData() || (this.hasData() && force)) {
      this._data = data;
      this.onDataAdded.emit(this._data);
    }
    return this;
  }

  public getData(): any {
    return this._data;
  }

  public contentChange(): void {
    if(this.modal) {
      this.modal.nativeElement.contentChange();
    }
  }

  public toggle(top?: boolean): RadBaseModalComponent {
    return this;
  }

  public open(top?: boolean): RadBaseModalComponent {
    this.modal.nativeElement.open();
    return this;
  }

  public newAction(): RadBaseModalComponent {
    return this;
  }

  public confirm(): RadBaseModalComponent {
    return this;
  }

  public dismiss(e: any): RadBaseModalComponent {
    return this;
  }

  public handleClose($event): void {
    this.onClose.emit($event);
    
    setTimeout(() => {
      this.onCloseFinished.emit($event);
    });
  }

  public handleNew($event): void {
    this.onNew.emit(this);
  }

  public handleConfirm($event): void {
    this.onConfirm.emit(this);
  }

  public close(): RadBaseModalComponent {
    this.handleClose(null);
    return this;
  }

  // Smart modal service expects this class but we don't need it for rad modal so this is a placeholder
  public addBodyClass(): RadBaseModalComponent {
    return this;
  }

  public addCustomClass(): RadBaseModalComponent {
    return this;
  }

  // Smart modal service expects this class but we don't need it for rad modal so this is a placeholder
  public removeBodyClass(): RadBaseModalComponent {
    return this;
  }

  ngOnDestroy() {
    clearTimeout(this.resizeTimer);
    // Clean up dynamically created component
    if (this.helpIconComponentRef) {
      this.helpIconComponentRef.destroy();
      this.helpIconComponentRef = null;
    }
  }
  
}
